import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertTriangle, ShieldAlert, CheckCircle2, Phone, BellRing, Check, Landmark, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import LanguageToggle from '../components/LanguageToggle';
import BottomNavigation from '../components/BottomNavigation';
import TownFollowPicker from '../components/TownFollowPicker';
import { useAuth, API } from '../App';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { missingSafetyFields, isSafetyFormReady } from '../utils/safetyFormValidation';

const TITLE_MAX = 250;
const BODY_MAX = 10000;

const SafetyBulletinSubmit = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { preferences, toggleArea, ensureSubscription } = useNotifications();

  const [towns, setTowns] = useState([]);
  const [town, setTown] = useState('');
  const [category, setCategory] = useState('safety_tip');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [followBusy, setFollowBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Iter 201 — Official-account profile (null until /user/profile resolves).
  // When set, the town picker is locked to `officialSafetyTown`.
  const [profile, setProfile] = useState(null);
  const isOfficial = !!(profile && (profile.officialSafetySource === 'town_hall' || profile.officialSafetySource === 'police'));
  const officialSource = profile?.officialSafetySource || null;
  const officialTown = profile?.officialSafetyTown || '';

  // The town actually used for validation + submission. For officials this is
  // derived DIRECTLY from their profile (no lagging state), so the submit
  // button enables the instant the profile loads — fixing a race where the
  // locked "Abla" label showed but the `town` state hadn't updated yet, leaving
  // the button silently disabled.
  const effectiveTown = isOfficial ? officialTown : town;
  // Iter 204 — Advanced (official-only) fields. Hidden behind a disclosure
  // toggle so the form stays simple for everyone else.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');     // datetime-local string ("YYYY-MM-DDTHH:MM")
  const [expiresAtIso, setExpiresAtIso] = useState('');   // datetime-local string
  const [priority, setPriority] = useState('normal');

  const followedTowns = preferences?.areaSubscriptions || [];
  const isFollowingSubmittedTown = success && followedTowns.includes(success.town);

  const loadTowns = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/safety-bulletin/towns`);
      setTowns(r.data.towns || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadTowns(); }, [loadTowns]);

  // Iter 201 — Load profile so we can detect official accounts and lock the
  // town picker. Fails silently for unauth users (page already gates them).
  useEffect(() => {
    if (!user?.token) { setProfile(null); return; }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/user/profile`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (alive) setProfile(r.data || null);
      } catch {
        if (alive) setProfile(null);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex flex-col">
        <header className="bg-slate-800 text-white px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} data-testid="safety-submit-back-btn"><ArrowLeft /></button>
          <LanguageToggle />
        </header>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl p-6 text-center max-w-sm shadow-sm border border-gray-200">
            <ShieldAlert className="h-10 w-10 text-slate-700 mx-auto mb-3" />
            <p className="text-gray-700 mb-4">{t('safety.submit.loginRequired')}</p>
            <Button onClick={() => navigate('/signin', { state: { from: `${location.pathname}${location.search || ''}` } })} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="safety-submit-signin-btn">
              {t('safety.submit.signIn')}
            </Button>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isSafetyFormReady({ effectiveTown, title, body, confirmed })) {
      toast.error(t('safety.submit.confirmCheckbox'));
      return;
    }
    setSubmitting(true);
    try {
      const lang = (i18n.language || 'en').slice(0, 2).toLowerCase() === 'es' ? 'es' : 'en';
      // Iter 204 — Officials may attach scheduledAt / expiresAt / priority.
      // We convert the local datetime-input value to a UTC ISO string before
      // sending, because the backend treats naive timestamps as UTC.
      const payload = {
        town: effectiveTown, category, title, body, language: lang,
        confirmedNoPersonalInfo: true,
      };
      if (isOfficial) {
        if (scheduledAt) payload.scheduledAt = new Date(scheduledAt).toISOString();
        if (expiresAtIso) payload.expiresAt = new Date(expiresAtIso).toISOString();
        if (priority && priority !== 'normal') payload.priority = priority;
      }
      const r = await axios.post(
        `${API}/safety-bulletin`,
        payload,
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      setSuccess(r.data.bulletin);
      // Reset
      setTitle(''); setBody(''); setConfirmed(false);
      setScheduledAt(''); setExpiresAtIso(''); setPriority('normal'); setAdvancedOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error(t('safety.submit.rateLimitHit'));
      } else {
        toast.error(detail || 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFollowSubmittedTown = async () => {
    if (!success?.town || followBusy) return;
    setFollowBusy(true);
    try {
      await ensureSubscription();
      await toggleArea(success.town);
      toast.success(
        t('safety.follow.nowFollowing', {
          town: success.town,
          defaultValue: "You'll get alerts for {{town}}",
        }),
      );
    } catch {
      toast.error(t('safety.follow.failed', 'Could not follow this town. Try again.'));
    } finally {
      setFollowBusy(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24" data-testid="safety-submit-success">
        <header className="bg-slate-800 text-white px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/safety-bulletin')} className="p-2 rounded hover:bg-white/10" data-testid="safety-submit-back-btn">
            <ArrowLeft className="h-5 w-5 rtl-flip" />
          </button>
          <LanguageToggle />
        </header>
        <main className="max-w-xl mx-auto px-4 py-10 space-y-4">
          <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-emerald-200">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('safety.submit.successTitle')}</h1>
            <p className="text-gray-600 mb-6">{t('safety.submit.successBody')}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={() => setSuccess(null)} variant="outline" data-testid="safety-submit-another">
                + {t('safety.submitCta')}
              </Button>
              <Button onClick={() => navigate('/safety-bulletin/mine')} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="safety-submit-view-mine">
                {t('safety.myPosts')}
              </Button>
            </div>
          </div>

          {/* "Want alerts next time?" nudge — only when the user isn't
              already following this town. */}
          {success.town && !isFollowingSubmittedTown && (
            <div
              className="bg-purple-50 border border-purple-200 rounded-xl p-5 flex items-start gap-3"
              data-testid="safety-submit-follow-nudge"
            >
              <div className="bg-purple-100 rounded-lg p-2 flex-shrink-0">
                <BellRing className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-purple-900">
                  {t('safety.follow.afterSubmitTitle', {
                    town: success.town,
                    defaultValue: 'Want alerts next time something is posted in {{town}}?',
                  })}
                </p>
                <p className="text-xs text-purple-700/80 mt-1">
                  {t('safety.follow.afterSubmitBody', "We'll ping you in the app when another safety alert is published.")}
                </p>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <Button
                    onClick={handleFollowSubmittedTown}
                    disabled={followBusy}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="safety-submit-follow-btn"
                  >
                    <BellRing className="h-4 w-4 mr-1" />
                    {followBusy
                      ? '…'
                      : t('safety.follow.followTown', { town: success.town, defaultValue: 'Follow {{town}}' })}
                  </Button>
                  <Button
                    onClick={() => setPickerOpen(true)}
                    size="sm"
                    variant="outline"
                    data-testid="safety-submit-more-towns-btn"
                  >
                    {t('safety.follow.pickMore', 'Pick more towns')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {success.town && isFollowingSubmittedTown && (
            <div
              className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2 text-sm text-emerald-900"
              data-testid="safety-submit-already-following"
            >
              <Check className="h-4 w-4 text-emerald-600" />
              {t('safety.follow.alreadyFollowing', {
                town: success.town,
                defaultValue: "You're already following {{town}} — we'll keep you posted.",
              })}
            </div>
          )}
        </main>
        <TownFollowPicker open={pickerOpen} onClose={() => setPickerOpen(false)} focusTown={success.town} />
        <BottomNavigation />
      </div>
    );
  }

  const titleLeft = TITLE_MAX - title.length;
  const bodyLeft = BODY_MAX - body.length;

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="safety-submit-page">
      <header className="bg-slate-800 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-white/10" data-testid="safety-submit-back-btn">
            <ArrowLeft className="h-5 w-5 rtl-flip" />
          </button>
          <h1 className="text-base font-semibold">{t('safety.submit.title')}</h1>
          <LanguageToggle />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-5" data-testid="safety-submit-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p>{t('safety.disclaimer')}</p>
              <a href="tel:112" className="inline-flex items-center gap-1 mt-2 text-amber-900 font-bold underline">
                <Phone className="h-4 w-4" /> 112
              </a>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 space-y-5" data-testid="safety-submit-form">
          <p className="text-sm text-gray-700">{t('safety.submit.intro')}</p>

          <div>
            <Label htmlFor="sb-town">{t('safety.submit.town')}</Label>
            {isOfficial ? (
              // Iter 201 — Locked to the admin-assigned town. We still send
              // the value via `town` state but the server also enforces it.
              <div
                className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-amber-300 bg-amber-50 text-sm text-amber-900"
                data-testid="safety-submit-town-locked"
              >
                {officialSource === 'police' ? <Shield className="h-4 w-4 text-amber-700" /> : <Landmark className="h-4 w-4 text-amber-700" />}
                <span className="font-semibold">{officialTown || '—'}</span>
                <span className="text-xs text-amber-700 ml-auto uppercase tracking-wide font-bold">
                  Posting as {profile?.fullName || 'Official source'}
                </span>
              </div>
            ) : (
              <select
                id="sb-town"
                required
                value={town}
                onChange={(e) => setTown(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
                data-testid="safety-submit-town"
              >
                <option value="">{t('safety.submit.townPlaceholder')}</option>
                {towns.map(tn => <option key={tn} value={tn}>{tn}</option>)}
              </select>
            )}
          </div>

          <div>
            <Label>{t('safety.submit.categoryLabel')}</Label>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="safety-submit-category-options">
              {['safety_tip', 'incident_factual', 'lost_pet', 'community_alert'].map((c) => {
                const examples = t(`safety.categoryExamples.${c}`, { returnObjects: true, defaultValue: [] });
                const isArr = Array.isArray(examples);
                return (
                  <label
                    key={c}
                    className={`flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer text-sm transition-colors ${
                      category === c
                        ? 'border-slate-700 bg-slate-50 ring-2 ring-slate-200'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    data-testid={`safety-submit-category-${c}`}
                  >
                    <input
                      type="radio"
                      name="category"
                      checked={category === c}
                      onChange={() => setCategory(c)}
                      className="mt-1 flex-shrink-0"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5 font-semibold text-gray-900">
                        <span aria-hidden="true">{t(`safety.categoryEmoji.${c}`)}</span>
                        <span>{t(`safety.category.${c}`)}</span>
                      </span>
                      <span className="block text-xs text-gray-600 mt-1 leading-relaxed">
                        {t(`safety.categoryHint.${c}`)}
                      </span>
                      {c === 'incident_factual' && (
                        <span
                          className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-[11px] font-semibold text-red-800"
                          data-testid="safety-submit-category-incident_factual-note"
                        >
                          <AlertTriangle className="h-3 w-3" />
                          {t('safety.categoryNote.incident_factual', 'No names. No accusations.')}
                        </span>
                      )}
                      {isArr && examples.length > 0 && (
                        <span className="block mt-2">
                          <span className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                            {t('safety.examplesLabel', 'Examples:')}
                          </span>
                          <ul className="mt-1 space-y-1 text-xs text-gray-600 italic">
                            {examples.map((ex, i) => (
                              <li key={i} className="flex gap-1.5">
                                <span className="text-gray-400 flex-shrink-0">•</span>
                                <span>{ex}</span>
                              </li>
                            ))}
                          </ul>
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            <p
              className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
              data-testid="safety-submit-category-footer"
            >
              {t('safety.submit.categoryFooter', 'Keep posts short, factual and helpful — no names, accusations or private information.')}
            </p>
          </div>

          <div>
            <Label htmlFor="sb-title">{t('safety.submit.titleLabel')}</Label>
            <Input
              id="sb-title"
              maxLength={TITLE_MAX}
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('safety.submit.titlePlaceholder')}
              data-testid="safety-submit-title"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{titleLeft}</p>
          </div>

          <div>
            <Label htmlFor="sb-body">{t('safety.submit.bodyLabel')}</Label>
            <Textarea
              id="sb-body"
              maxLength={BODY_MAX}
              required
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('safety.submit.bodyPlaceholder')}
              rows={8}
              className="resize-y min-h-[180px]"
              data-testid="safety-submit-body"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{bodyLeft}</p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800" data-testid="safety-submit-rules">
            <p className="font-semibold mb-2">{t('safety.submit.rules')}</p>
            <ul className="space-y-1 list-disc list-inside text-slate-700">
              <li>{t('safety.submit.rulesNoNames')}</li>
              <li>{t('safety.submit.rulesNoAccusations')}</li>
              <li>{t('safety.submit.rulesNoPlates')}</li>
              <li>{t('safety.submit.rulesNoPrivate')}</li>
            </ul>
          </div>

          {/* Iter 204 — Advanced options (officials only).
              Disclosure toggle keeps the resident submit flow simple. */}
          {isOfficial && (
            <div className="border border-amber-200 rounded-lg bg-amber-50/40" data-testid="safety-submit-advanced">
              <button
                type="button"
                onClick={() => setAdvancedOpen(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-amber-50 transition-colors rounded-lg"
                data-testid="safety-submit-advanced-toggle"
                aria-expanded={advancedOpen}
              >
                <span className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-700" />
                  {i18n.language === 'es' ? 'Opciones avanzadas (autoridad oficial)' : 'Advanced options (Official Authority)'}
                </span>
                <span className="text-xs text-amber-700">{advancedOpen ? '−' : '+'}</span>
              </button>
              {advancedOpen && (
                <div className="px-3 pb-3 space-y-3">
                  <div>
                    <Label htmlFor="sb-priority" className="text-xs font-semibold text-amber-900">
                      {i18n.language === 'es' ? 'Prioridad' : 'Priority'}
                    </Label>
                    <select
                      id="sb-priority"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-sm"
                      data-testid="safety-submit-priority"
                    >
                      <option value="low">🔵 {i18n.language === 'es' ? 'Baja (sin notificación push)' : 'Low (no push notification)'}</option>
                      <option value="normal">⚪ {i18n.language === 'es' ? 'Normal' : 'Normal'}</option>
                      <option value="high">🟠 {i18n.language === 'es' ? 'Alta' : 'High'}</option>
                      <option value="critical">🔴 {i18n.language === 'es' ? 'Crítica (evacuación, emergencia)' : 'Critical (evacuation, emergency)'}</option>
                    </select>
                    <p className="text-[11px] text-amber-700 mt-1">
                      {priority === 'critical'
                        ? (i18n.language === 'es' ? 'Aparece en rojo en la parte superior del feed con notificación push urgente.' : 'Renders as a red top-of-feed banner with an urgent push notification.')
                        : priority === 'high'
                          ? (i18n.language === 'es' ? 'Marcado en naranja y priorizado en el feed.' : 'Orange ribbon and prioritised in the feed.')
                          : priority === 'low'
                            ? (i18n.language === 'es' ? 'Aparece en el feed sin enviar notificación push (avisos informativos no urgentes).' : 'Appears in the feed without sending a push notification (informational only).')
                            : (i18n.language === 'es' ? 'Comportamiento estándar.' : 'Standard behaviour.')}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="sb-scheduled" className="text-xs font-semibold text-amber-900">
                      {i18n.language === 'es' ? 'Programar publicación (opcional)' : 'Schedule publication (optional)'}
                    </Label>
                    <input
                      id="sb-scheduled"
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-sm"
                      data-testid="safety-submit-scheduled-at"
                    />
                    <p className="text-[11px] text-amber-700 mt-1">
                      {i18n.language === 'es'
                        ? 'Si lo dejas vacío, se publica inmediatamente. Máximo 30 días por adelantado.'
                        : 'Leave blank to publish now. Maximum 30 days ahead.'}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="sb-expires" className="text-xs font-semibold text-amber-900">
                      {i18n.language === 'es' ? 'Caducidad personalizada (opcional)' : 'Custom expiry (optional)'}
                    </Label>
                    <input
                      id="sb-expires"
                      type="datetime-local"
                      value={expiresAtIso}
                      onChange={(e) => setExpiresAtIso(e.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-sm"
                      data-testid="safety-submit-expires-at"
                    />
                    <p className="text-[11px] text-amber-700 mt-1">
                      {i18n.language === 'es'
                        ? 'Por defecto: 24 h (avisos), 14 días (incidentes), 30 días (mascotas), 90 días (consejos).'
                        : 'Defaults: 24h alerts · 14d incidents · 30d lost pets · 90d safety tips.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer" data-testid="safety-submit-confirm-label">
            <Checkbox
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(v === true)}
              data-testid="safety-submit-confirm-checkbox"
              className="mt-0.5"
            />
            <span>{t('safety.submit.confirmCheckbox')}</span>
          </label>

          {/* Inline validation hints — so the disabled button is never a
              silent dead-end. Lists exactly what is still required. */}
          {(() => {
            const labels = {
              town: t('safety.submit.needTown', 'Select a town'),
              title: t('safety.submit.needTitle', 'Add a title (at least 4 characters)'),
              body: t('safety.submit.needBody', 'Add details (at least 10 characters)'),
              confirm: t('safety.submit.needConfirm', 'Tick the confirmation box'),
            };
            const missing = missingSafetyFields({ effectiveTown, title, body, confirmed });
            if (missing.length === 0) return null;
            return (
              <ul className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1" data-testid="safety-submit-hints">
                {missing.map((k) => <li key={k}>• {labels[k]}</li>)}
              </ul>
            );
          })()}

          <Button
            type="submit"
            disabled={submitting || !isSafetyFormReady({ effectiveTown, title, body, confirmed })}
            className="w-full bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="safety-submit-button"
          >
            {submitting ? t('safety.submit.submitting') : t('safety.submit.submitButton')}
          </Button>
        </form>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default SafetyBulletinSubmit;
